// AlertCapsule — compact, toned alert pill used in dense alert rows (e.g. the
// landing page's plan-alert strip). Smaller and denser than TipCard: a leading
// tone icon (or dot), a bold label, an optional headline line, an optional
// chevron affordance when clickable, and an optional dismiss button.
//
// Clickable capsules render as a div with role="button" (so the dismiss button
// can nest without an invalid button-in-button); keyboard activation is wired
// for Enter/Space.
import PropTypes from 'prop-types';
import { cx } from '../../../utils/cx';
import { InfoIcon, WarnIcon, CheckIcon, CloseIcon, CaretIcon } from '../../icons/Icons';
import './alert-capsule.css';

const TONE_ICON = {
  danger: WarnIcon,
  warning: WarnIcon,
  info: InfoIcon,
  success: CheckIcon,
  neutral: InfoIcon,
};

export function AlertCapsule({
  tone = 'neutral',
  label,
  headline,
  icon,
  dot = false,
  onClick,
  onDismiss,
  chevron,
  className,
  children,
}) {
  const clickable = typeof onClick === 'function';
  // Show the affordance chevron when explicitly requested, else when clickable.
  const showChevron = chevron ?? clickable;
  const ToneIcon = TONE_ICON[tone] || InfoIcon;
  const body = headline ?? children;

  const handleKeyDown = (e) => {
    if (!clickable) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick(e);
    }
  };

  return (
    <div
      className={cx('alert-capsule', `alert-capsule--${tone}`, clickable && 'is-clickable', className)}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? onClick : undefined}
      onKeyDown={clickable ? handleKeyDown : undefined}
    >
      <div className="alert-capsule__top">
        {dot ? (
          <span className="alert-capsule__dot" aria-hidden="true" />
        ) : (
          <span className="alert-capsule__tone" aria-hidden="true">
            {icon || <ToneIcon width={14} height={14} />}
          </span>
        )}
        {label && <span className="alert-capsule__label">{label}</span>}
        {onDismiss && (
          <button
            type="button"
            className="alert-capsule__dismiss"
            onClick={(e) => {
              e.stopPropagation();
              onDismiss(e);
            }}
            aria-label="Dismiss"
          >
            <CloseIcon width={12} height={12} />
          </button>
        )}
      </div>
      {body && (
        <p className="alert-capsule__headline">
          <span className="alert-capsule__headline-text">{body}</span>
          {showChevron && (
            <span className="alert-capsule__chev" aria-hidden="true">
              <CaretIcon width={11} height={11} />
            </span>
          )}
        </p>
      )}
    </div>
  );
}

AlertCapsule.propTypes = {
  tone: PropTypes.oneOf(['danger', 'warning', 'info', 'success', 'neutral']),
  label: PropTypes.node,
  headline: PropTypes.node,
  icon: PropTypes.node,
  dot: PropTypes.bool,
  onClick: PropTypes.func,
  onDismiss: PropTypes.func,
  /** Force the affordance chevron on/off; defaults to whether onClick is set. */
  chevron: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
};

export default AlertCapsule;
